// File: src/pages/reports/PurchaseReportAmount.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Label,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";

/* ===== Your shared modules (per rules) ===== */
import { PurchaseOrderList as _PurchaseOrderList } from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import SuperAdminUI from "../import/SuperAdminUI";

/* ========= inline, page-scoped CSS ========= */
const injectOnce = () => {
  if (document.getElementById("ppwr-inline-css")) return;
  const style = document.createElement("style");
  style.id = "ppwr-inline-css";
  style.innerHTML = `
    .ppwr-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .ppwr-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .ppwr-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1100px; }
    .ppwr-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .ppwr-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .ppwr-table tfoot td{
      padding:8px; font-weight:800; background:#fbfbff; border-top:1px solid #e5e7eb;
    }
    .ppwr-table tbody tr:hover{ background:#f9fbff; }
    .ppwr-dl{ position:relative; }
    .ppwr-dl-menu{
      position:absolute; right:0; top:32px; z-index:5; border:1px solid rgb(8,91,245);
      background:#fff; border-radius:10px; overflow:hidden; min-width:130px; box-shadow:0 12px 24px rgba(0,0,0,.12);
    }
    .ppwr-dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
  `;
  document.head.appendChild(style);
};

/* ========= helpers ========= */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const f2 = (n) =>
  n === null || n === undefined || isNaN(n) ? "" : Number(n).toFixed(2);
const formatDateISO = (d) => (d ? String(d).split("T")[0] : "");
const formatDDMMMYYYY = (iso) => {
  if (!iso) return "-";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ========= component ========= */
export default function PurchaseReportAmount() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: false,
    Edit: false,
    Delete: false,
    Download: true,
  });

  const [isMaster, setIsMaster] = useState(false);

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  const [search, setSearch] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);

  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [selectedProduct, setSelectedProduct] = useState("");
  const [productOptions, setProductOptions] = useState([]);

  // Column chooser buffers
  const addBufferRef = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => injectOnce(), []);

  /* ===== Base columns (mirrors your grid) ===== */
  const baseColumns = useMemo(
    () => [
      {
        key: "uid",
        header: "UID",
        get: (_r, idx) => idx + 1,
      },
      {
        key: "date",
        header: "DATE",
        get: (r) => formatDDMMMYYYY(r?.order?.date),
      },
      {
        key: "invoice",
        header: "INVOICE NUMBER",
        get: (r) => r?.order?.invoiceId,
      },
      {
        key: "partyName",
        header: "PARTY NAME",
        get: (r) => r?.order?.partyId?.CompanyName,
      },
      {
        key: "product",
        header: "PRODUCT NAME",
        get: (r) => r?.productId?.Product_Title,
      },
      {
        key: "hsn",
        header: "HSN",
        get: (r) => r?.productId?.HSN_Code,
      },
      {
        key: "ownerName",
        header: "PARTY OWNER NAME",
        get: (r) => r?.order?.partyId?.ownerName,
      },
      {
        key: "qty",
        header: "QNTY",
        get: (r) => r?.qty,
      },
      {
        key: "basicPrice",
        header: "BASIC PRICE",
        get: (r) => r?.basicPrice,
      },
      {
        key: "expenses",
        header: "EXPENSES",
        get: (r) => {
          const o = r?.order || {};
          return (
            Number(o.coolieAndCartage || 0) +
            Number(o.labourCost || 0) +
            Number(o.localFreight || 0) +
            Number(o.miscellaneousCost || 0) +
            Number(o.transportationCost || 0) +
            Number(o.tax || 0)
          );
        },
      },
      {
        key: "purchaseRate",
        header: "PURCHASE RATE",
        get: (r) => {
          const o = r?.order || {};
          const expenses =
            Number(o.coolieAndCartage || 0) +
            Number(o.labourCost || 0) +
            Number(o.localFreight || 0) +
            Number(o.miscellaneousCost || 0) +
            Number(o.transportationCost || 0) +
            Number(o.tax || 0);
          const q = Number(r?.qty || 0);
          const taxable = Number(r?.taxableAmount || 0);
          return q ? f2((taxable + expenses) / q) : "";
        },
      },
      {
        key: "discountPercentage",
        header: "DISCOUNT(%)",
        get: (r) => r?.discountPercentage,
      },
      {
        key: "taxableAmount",
        header: "TAXABLE",
        get: (r) => r?.taxableAmount,
      },
      {
        key: "cgstRate",
        header: "CGST",
        get: (r) => r?.cgstRate,
      },
      {
        key: "sgstRate",
        header: "SGST",
        get: (r) => r?.sgstRate,
      },
      {
        key: "igstRate",
        header: "IGST",
        get: (r) => r?.igstRate,
      },
      {
        key: "grandTotal",
        header: "TOTAL",
        get: (r) => r?.grandTotal,
      },
    ],
    []
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  // restore visible columns (keep your original key)
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("PurchaseReportamountList");
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        if (typeof parsed[0] === "string") {
          nextKeys = parsed.filter((k) => colMap.has(k));
        } else if (typeof parsed[0] === "object") {
          const byHeader = new Map(
            baseColumns.map((c) => [c.header?.toUpperCase(), c.key])
          );
          nextKeys = parsed
            .map((obj) =>
              byHeader.get(String(obj?.headerName || "").toUpperCase())
            )
            .filter((k) => !!k && colMap.has(k));
        }
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [colMap, baseColumns]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  /* ===== Fetch ===== */
  const fetchData = async (userId, database) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const inside = CheckPermission(
        "PARTY WISE AND PRODUCT WISE PURCHASE REPORT"
      );
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }

      const res = await _PurchaseOrderList(userId, database);
      const completed = Array.isArray(res?.orderHistory)
        ? res.orderHistory.filter(
            (o) => String(o?.status).toLowerCase() === "completed"
          )
        : [];

      const flat = completed.flatMap((ord) =>
        Array.isArray(ord?.orderItems)
          ? ord.orderItems.map((itm) => ({ ...itm, order: ord }))
          : []
      );

      setRows(flat);
      setAllRows(flat);

      const uniqTitles = Array.from(
        new Set(
          flat
            .map((x) => x?.productId?.Product_Title)
            .filter((s) => typeof s === "string" && s.trim().length)
        )
      );
      setProductOptions(uniqTitles);
    } catch (e) {
      console.error("[PPWR] fetch error:", e);
      setErrorMsg(
        e?.response?.data?.message || e?.message || "Failed to load report."
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  // bootstrap
  useEffect(() => {
    (async () => {
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        setIsMaster(user?.rolename?.roleName === "MASTER");
        await fetchData(user?._id, user?.database);
      } catch (e) {
        setLoading(false);
      }
    })();
  }, []);

  /* ===== Filters (date + product + search) ===== */
  const filteredByDate = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return rows;
    return rows.filter((item) => {
      const dt = item?.order?.date ? formatDateISO(item.order.date) : "";
      if (startDate && dt < startDate) return false;
      if (endDate && dt > endDate) return false;
      return true;
    });
  }, [rows, dateRange]);

  const filteredByProduct = useMemo(() => {
    if (!selectedProduct) return filteredByDate;
    return filteredByDate.filter(
      (r) => r?.productId?.Product_Title === selectedProduct
    );
  }, [filteredByDate, selectedProduct]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredByProduct;
    const cols = visibleCols;
    return filteredByProduct.filter((r, idx) =>
      cols.some((c) => toStr(c.get(r, idx)).toLowerCase().includes(term))
    );
  }, [filteredByProduct, search, visibleCols]);

  /* ===== Totals over filtered rows ===== */
  const totals = useMemo(() => {
    let qty = 0,
      basic = 0,
      taxable = 0,
      cgst = 0,
      sgst = 0,
      igst = 0,
      total = 0,
      expenses = 0;
    filteredRows.forEach((r) => {
      qty += Number(r?.qty || 0);
      basic += Number(r?.basicPrice || 0);
      taxable += Number(r?.taxableAmount || 0);
      cgst += Number(r?.cgstRate || 0);
      sgst += Number(r?.sgstRate || 0);
      igst += Number(r?.igstRate || 0);
      total += Number(r?.grandTotal || 0);
      const o = r?.order || {};
      expenses +=
        Number(o.coolieAndCartage || 0) +
        Number(o.labourCost || 0) +
        Number(o.localFreight || 0) +
        Number(o.miscellaneousCost || 0) +
        Number(o.transportationCost || 0) +
        Number(o.tax || 0);
    });
    const purchaseRate =
      qty > 0 ? f2((Number(taxable) + Number(expenses)) / qty) : "";
    return {
      qty,
      basic,
      taxable,
      cgst,
      sgst,
      igst,
      total,
      expenses,
      purchaseRate,
    };
  }, [filteredRows]);

  /* ===== Column chooser ===== */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);
  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem(
      "PurchaseReportamountList",
      JSON.stringify(selectedKeys)
    );
    closeModal();
  };

  /* ===== MASTER → SuperAdminUI handlers ===== */
  const handleParentSubmit = async () => {
    const saved = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!saved) return swal("Please pick a SuperAdmin in the dropdown");
    let sid = "";
    let sdb = "";
    if (typeof saved === "string") {
      const parts = saved.split(" ");
      sid = parts[0];
      sdb = parts[1];
    } else if (Array.isArray(saved)) {
      sid = saved[0];
      sdb = saved[1];
    }
    if (!sid || !sdb) return swal("Invalid Master selection");
    await fetchData(sid, sdb);
  };
  const handleDropdownChange = (v) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(v));
  };

  /* ===== Export ===== */
  const handleExportPDF = async () => {
    const cols = visibleCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r, idx) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r, idx)).replace(/"/g, '""');
          return "${v}";
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "Party_Product_Purchase_Report");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols;
    const data = filteredRows.map((r, idx) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r, idx))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  /* ===== UI ===== */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar card ===== */}
      <Row className="ppwr-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 16,
                }}
              >
                Party Wise & Product Wise Purchase Report{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 80px" }} />

              {/* MASTER selector */}
              {isMaster ? (
                <div style={{ minWidth: 260 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : null}

              {/* Product filter */}
              <div style={{ minWidth: 220 }}>
                <Input
                  type="select"
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                >
                  <option value="">Select a product</option>
                  {productOptions.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </Input>
              </div>

              {/* Search */}
              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {/* Date range */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <Label className="m-0">Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) =>
                      setDateRange((p) => ({ ...p, startDate: e.target.value }))
                    }
                    style={{ background: "#fff", minWidth: 160 }}
                  />
                </div>
                <div>
                  <Label className="m-0">End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) =>
                      setDateRange((p) => ({ ...p, endDate: e.target.value }))
                    }
                    style={{ background: "#fff", minWidth: 160 }}
                  />
                </div>
                <Button
                  style={{
                    cursor: "pointer",
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => setDateRange((p) => ({ ...p }))}
                >
                  Apply
                </Button>
              </div>

              {/* Column chooser */}
              {permissions?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openModal}
                  title="Filter Columns"
                  style={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFilter size={14} />
                </Button>
              )}

              {/* Download */}
              {permissions?.Download && (
                <div
                  className="ppwr-dl"
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div className="ppwr-dl-menu">
                      <div onClick={handleExportPDF} className="ppwr-dl-item">
                        .PDF
                      </div>
                      <div
                        onClick={() =>
                          handleExportXLSX("Party_Product_Purchase_Report")
                        }
                        className="ppwr-dl-item"
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table card ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="ppwr-table-wrap">
                <table className="ppwr-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, idx) => (
                      <tr
                        key={
                          r?._id || r?.id || `${r?.order?._id || "row"}-${idx}`
                        }
                      >
                        {visibleCols.map((c) => (
                          <td key={c.key}>{toStr(c.get(r, idx))}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      {visibleCols.map((c) => {
                        switch (c.key) {
                          case "invoice":
                            return <td key={c.key}>Total</td>;
                          case "qty":
                            return <td key={c.key}>{totals.qty}</td>;
                          case "basicPrice":
                            return <td key={c.key}>{f2(totals.basic)}</td>;
                          case "expenses":
                            return <td key={c.key}>{f2(totals.expenses)}</td>;
                          case "purchaseRate":
                            return <td key={c.key}>{totals.purchaseRate}</td>;
                          case "taxableAmount":
                            return <td key={c.key}>{f2(totals.taxable)}</td>;
                          case "cgstRate":
                            return <td key={c.key}>{f2(totals.cgst)}</td>;
                          case "sgstRate":
                            return <td key={c.key}>{f2(totals.sgst)}</td>;
                          case "igstRate":
                            return <td key={c.key}>{f2(totals.igst)}</td>;
                          case "grandTotal":
                            return <td key={c.key}>{f2(totals.total)}</td>;
                          default:
                            return <td key={c.key}></td>;
                        }
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column chooser modal ===== */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* small styles reused */
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
